import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useCart, canEditCart } from "../context/CartContext";
import { WarehouseSelect } from "./WarehouseSelect";
import { formatApiErrorDetail } from "../lib/api";
import { LAST_WAREHOUSE_KEY } from "../lib/useWarehouses";
import { Settings as SettingsIcon, Users, LogOut, Receipt, ChevronDown, Check, Plus, List, ShoppingCart, FileCode2, Lock } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import LanguageToggle from "./LanguageToggle";

const LATEST_QUOTES_LIMIT = 10;

export default function Topbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { carts, activeCart, activeCartId, setActiveCartId, createCart } = useCart();
  const [newQuoteWarehouse, setNewQuoteWarehouse] = useState(() => localStorage.getItem(LAST_WAREHOUSE_KEY) || null);
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  // iter 215 (2026-02): user_admin 也能访问 /users 页,但不能进 Settings。
  const canManageUsers = isAdmin || user?.role === "user_admin";
  const [newQuoteOpen, setNewQuoteOpen] = useState(false);
  const [newQuoteName, setNewQuoteName] = useState("");

  const latestQuotes = [...carts]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, LATEST_QUOTES_LIMIT);

  const handleLogout = async () => {
    await logout();
    toast.success(t("login.signed_out_success"));
    navigate("/login");
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    const name = newQuoteName.trim();
    if (!name) return;
    try {
      await createCart(name, { sample_warehouse_id: newQuoteWarehouse });
      if (newQuoteWarehouse) localStorage.setItem(LAST_WAREHOUSE_KEY, newQuoteWarehouse);
      toast.success(`${t("topbar.new_quotation")} "${name}"`);
      setNewQuoteOpen(false);
      setNewQuoteName("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("samples.failed"));
    }
  };

  return (
    <header className="h-8 flex-shrink-0 bg-white border-b border-gray-200 flex items-center justify-end gap-1.5 px-4" data-testid="app-topbar">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="current-quotation-trigger"
            className="relative flex items-center gap-1 h-6 pl-7 pr-2 rounded-md border border-gray-200 text-[12px] font-medium text-gray-800 hover:bg-gray-50 hover:text-gray-900 transition-colors max-w-[200px]"
          >
            {/* iter 178y: cart icon + item-count badge on the left, matching
                the reference layout. Badge only renders when cart has items.
                iter 181 (2026-02): compact 32px topbar — icons/paddings
                halved from the previous 64px version. */}
            <span className="absolute left-1.5 top-1/2 -translate-y-1/2 flex items-center">
              <ShoppingCart size={12} strokeWidth={1.75} className="text-gray-500" />
              {(activeCart?.item_count ?? 0) > 0 && (
                <span
                  data-testid="topbar-cart-badge"
                  className="absolute -top-1 -right-1.5 min-w-[13px] h-[13px] px-0.5 rounded-full bg-brand-600 text-[9px] font-semibold text-white flex items-center justify-center leading-none"
                >
                  {activeCart.item_count > 99 ? "99+" : activeCart.item_count}
                </span>
              )}
            </span>
            <span className="truncate font-medium">{activeCart?.name || t("topbar.no_quotation")}</span>
            {activeCart && !canEditCart(activeCart) && <Lock size={10} strokeWidth={2} className="flex-shrink-0 text-amber-600" data-testid="topbar-cart-readonly" />}
            {activeCart?.sample_warehouse_name && <span className="flex-shrink-0 px-1 rounded bg-gray-100 text-gray-500 text-[9px] font-medium truncate max-w-[70px]" data-testid="topbar-cart-warehouse">{activeCart.sample_warehouse_name}</span>}
            <ChevronDown size={11} strokeWidth={1.75} className="flex-shrink-0 text-gray-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 rounded-lg">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-label text-gray-400">
            {t("topbar.switch_quotation")} ({latestQuotes.length})
          </DropdownMenuLabel>
          {latestQuotes.map((c) => (
            <DropdownMenuItem
              key={c.id}
              data-testid={`current-quotation-option-${c.id}`}
              onClick={() => setActiveCartId(c.id)}
              className={`flex items-center justify-between gap-2 ${c.id === activeCartId ? "bg-brand-50 text-brand-700 font-medium" : ""}`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <Check size={14} strokeWidth={2} className={`flex-shrink-0 ${c.id === activeCartId ? "opacity-100" : "opacity-0"}`} />
                <span className="truncate">{c.name}</span>
                {c.owner?.name && c.owner.user_id !== user?.id && <span className="flex-shrink-0 px-1 rounded bg-brand-50 text-brand-700 text-[9px] truncate max-w-[70px]" data-testid={`current-quotation-owner-${c.id}`}>{c.owner.name}</span>}
                {c.sample_warehouse_name && <span className="flex-shrink-0 px-1 rounded bg-gray-100 text-gray-500 text-[9px]">{c.sample_warehouse_name}</span>}
              </span>
              <span className="text-xs text-gray-400 flex-shrink-0">{c.item_count}</span>
            </DropdownMenuItem>
          ))}
          {latestQuotes.length === 0 && (
            <DropdownMenuItem disabled>{t("cart.no_quotations")}</DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="current-quotation-new-quote"
            onClick={() => setNewQuoteOpen(true)}
            className="gap-2 font-medium text-gray-800"
          >
            <Plus size={14} strokeWidth={2} /> {t("topbar.new_quotation")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="current-quotation-all-quotes"
            onClick={() => navigate("/cart")}
            className="gap-2 font-medium text-gray-800"
          >
            <List size={14} strokeWidth={2} /> {t("topbar.view_all_quotations")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="flex items-center gap-1.5 mr-1 leading-none">
        <span className="font-mono text-[11px] text-gray-700 truncate max-w-[180px]" data-testid="header-user-email">{user?.email}</span>
        <span className="text-[9px] uppercase tracking-label text-gray-400">{user?.role}</span>
      </div>
      {isAdmin && (
        <button
          type="button"
          data-testid="nav-system-templates-button"
          onClick={() => toast.info(t("topbar.system_templates_coming"))}
          className="flex items-center gap-1 h-6 px-2 rounded-md border border-gray-200 text-[12px] font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
        >
          <FileCode2 size={12} strokeWidth={1.75} /> {t("topbar.system_templates")}
        </button>
      )}
      {canManageUsers && (
        <Link
          to="/users"
          data-testid="nav-users-link"
          className="flex items-center gap-1 h-6 px-2 rounded-md border border-gray-200 text-[12px] font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
        >
          <Users size={12} strokeWidth={1.75} /> {t("nav.user_management")}
        </Link>
      )}
      {isAdmin && (
        <Link
          to="/settings"
          data-testid="nav-settings-link"
          className="flex items-center gap-1 h-6 px-2 rounded-md border border-gray-200 text-[12px] font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
        >
          <SettingsIcon size={12} strokeWidth={1.75} /> {t("nav.settings")}
        </Link>
      )}
      <LanguageToggle />
      <button
        data-testid="logout-button"
        onClick={handleLogout}
        className="flex items-center gap-1 h-6 px-2 rounded-md text-[12px] text-gray-500 hover:text-gray-900 transition-colors"
      >
        <LogOut size={12} strokeWidth={1.75} /> {t("nav.sign_out")}
      </button>

      <Dialog open={newQuoteOpen} onOpenChange={setNewQuoteOpen}>
        <DialogContent className="sm:max-w-md" data-testid="topbar-new-quote-dialog">
          <DialogHeader>
            <DialogTitle>{t("topbar.new_quotation")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateQuote} className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-gray-600">{t("cart.quotation_no")}</Label>
              <Input
                autoFocus
                value={newQuoteName}
                onChange={(e) => setNewQuoteName(e.target.value)}
                placeholder=""
                data-testid="topbar-new-quote-name-input"
                className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
              />
            </div>
            {user?.role !== "sample_warehouse" && (
              <div>
                <Label className="text-xs font-medium text-gray-600">{t("warehouse.title_short")}</Label>
                <WarehouseSelect value={newQuoteWarehouse} onChange={setNewQuoteWarehouse} testId="topbar-new-quote-warehouse" className="mt-1.5" />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewQuoteOpen(false)} className="rounded-lg">{t("common.cancel")}</Button>
              <Button type="submit" data-testid="topbar-new-quote-submit" className="rounded-lg bg-brand-600 hover:bg-brand-700">{t("common.create")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </header>
  );
}
